import axiosInstance from './axios';
import { getToken, removeToken } from '../utils/tokenStorage';

/**
 * Axios Interceptors
 * Handles automatic token attachment and global error handling.
 */

export const setupInterceptors = () => {
  // Request Interceptor: Attach token to every request
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Prevent caching for GET requests by appending a timestamp
      if (config.method === 'get') {
        config.params = {
          ...config.params,
          _t: new Date().getTime(),
        };
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor: Global error handling & Data Transformation
  axiosInstance.interceptors.response.use(
    (response) => {
      // Image URL Transformation & Field Removal
      const transformData = (data) => {
        if (!data) return data;
        
        // Handle Strings (URLs)
        if (typeof data === 'string') {
          // Rule 1: Handle specific Backblaze placeholder
          if (data.includes('https://f006.backblazeb2.com/file/1234Tour/')) {
            return data.replace('https://f006.backblazeb2.com/file/1234Tour/', 'https://tourassets.wildnorthernjourney.com/');
          }
          // Rule 2: Handle general Backblaze base URL
          if (data.includes('https://f006.backblazeb2.com/file/')) {
            return data.replace('https://f006.backblazeb2.com/file/', 'https://tourassets.wildnorthernjourney.com/');
          }
          // Rule 3: Handle Bunny CDN base URL (found in private transfers)
          if (data.includes('https://tourassets.b-cdn.net/')) {
            return data.replace('https://tourassets.b-cdn.net/', 'https://tourassets.wildnorthernjourney.com/');
          }
          return data;
        }
        
        // Handle Arrays
        if (Array.isArray(data)) {
          return data.map(transformData);
        }
        
        // Handle Objects
        if (typeof data === 'object') {
          const newData = {};
          for (const key in data) {
            // Remove image field specifically for Tour Types (heuristic: has name/description but not package-specific fields like includes/excludes)
            // Or just check if we are on a tour-type related endpoint if possible.
            // But simple approach: if it has 'isActive' and 'name' and we want to remove 'image' globally for these objects.
            if (key === 'image' && data.name && !data.includes && !data.duration) {
              continue; 
            }
            newData[key] = transformData(data[key]);
          }
          return newData;
        }
        return data;
      };

      if (response.data) {
        response.data = transformData(response.data);
      }
      
      return response;
    },
    (error) => {
      const status = error.response ? error.response.status : null;
      const errorMsgText = error.response?.data?.message || error.message || '';
      const isTokenExpired = errorMsgText.toLowerCase().includes('jwt expired') || 
                             errorMsgText.toLowerCase().includes('token expired') ||
                             errorMsgText.toLowerCase().includes('token has expired');

      if (status === 401 || isTokenExpired) {
        // Unauthorized/Token Expired: Clear token, all storage, and redirect to login
        removeToken();
        if (typeof window !== 'undefined') {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/';
        }
      } else if (status === 403) {
        console.error('Permission denied (403)');
      } else if (status === 500) {
        console.error('Internal Server Error (500)');
      }

      // Return a consistent error object
      const errorMessage = error.response?.data?.message || 'Something went wrong';
      return Promise.reject({
        message: errorMessage,
        status: status,
        originalError: error
      });
    }
  );
};
